
import { supabase } from "@/integrations/supabase/client";
import { hostSettingsCache } from "@/lib/performance/enhanced-request-cache";
import { createPerformanceLogger } from "@/lib/utils/performance-logger";
import type { ProfileData } from "@/hooks/shared/use-profile-data";

const logger = createPerformanceLogger('EnhancedSettingsService');

export interface HostAvailabilitySettings {
  bufferBefore: number;
  bufferAfter: number;
  minNoticeTime: number;
  timezone?: string;
  userEnergySettings?: any;
  wakeTime?: number;
}

// Type for energy profile data
interface EnergyProfile {
  wakeTime?: number;
  [key: string]: any;
}

/**
 * Enhanced settings service with improved caching and cache invalidation
 * @param userId - User ID to fetch settings for
 * @param profileData - Optional profile data to avoid duplicate fetching
 */
export async function fetchHostAvailabilitySettings(
  userId: string, 
  profileData?: ProfileData | null
): Promise<HostAvailabilitySettings> {
  const cacheKey = `host_settings_${userId}`;
  const cached = hostSettingsCache.get(cacheKey);
  
  if (cached) {
    logger.debug(`Host settings cache hit for user ${userId}`);
    return cached as HostAvailabilitySettings;
  }

  logger.debug(`Host settings cache miss for user ${userId}, fetching from database`);

  if (userId === 'debug') {
    const debugSettings: HostAvailabilitySettings = {
      bufferBefore: 15,
      bufferAfter: 15,
      minNoticeTime: 60,
      timezone: 'America/Chicago',
      wakeTime: 7
    };
    hostSettingsCache.set(cacheKey, debugSettings);
    return debugSettings;
  }

  try {
    // Fetch availability settings
    const { data: availabilityData, error: availabilityError } = await supabase
      .from('availability_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (availabilityError) {
      logger.error(`Error fetching availability settings: ${availabilityError.message}`);
    }

    // Use provided profile data or fetch it if not provided
    let actualProfileData = profileData;
    if (!actualProfileData) {
      logger.debug(`Profile data not provided, fetching for user ${userId}`);
      const { data: fetchedProfileData, error: profileError } = await supabase
        .from('profiles')
        .select('energy_profile')
        .eq('id', userId)
        .maybeSingle();

      if (profileError) {
        logger.error(`Error fetching profile: ${profileError.message}`);
      }
      // Transform raw Supabase data to ProfileData format
      actualProfileData = fetchedProfileData ? {
        ...fetchedProfileData,
        energy_profile: typeof fetchedProfileData.energy_profile === 'object' 
          ? fetchedProfileData.energy_profile 
          : typeof fetchedProfileData.energy_profile === 'string'
            ? JSON.parse(fetchedProfileData.energy_profile)
            : null
      } as ProfileData : null;
    } else {
      logger.debug(`Using provided profile data for user ${userId}`);
    }

    // Safely parse energy profile data from actual profile data
    let energyProfile: EnergyProfile | null = null;
    let wakeTime = 7; // Default wake time

    if (actualProfileData?.energy_profile) {
      try {
        // Handle both string and object cases for energy_profile
        if (typeof actualProfileData.energy_profile === 'string') {
          energyProfile = JSON.parse(actualProfileData.energy_profile);
        } else if (typeof actualProfileData.energy_profile === 'object') {
          energyProfile = actualProfileData.energy_profile as EnergyProfile;
        }
        
        // Extract wake time if available
        if (energyProfile && typeof energyProfile.wakeTime === 'number') {
          wakeTime = energyProfile.wakeTime;
        }
      } catch (parseError) {
        logger.error(`Error parsing energy profile: ${parseError}`);
      }
    }

    const settings: HostAvailabilitySettings = {
      bufferBefore: availabilityData?.buffer_before || 15,
      bufferAfter: availabilityData?.buffer_after || 15,
      minNoticeTime: availabilityData?.min_notice_time || 60,
      timezone: availabilityData?.timezone || 'UTC',
      userEnergySettings: energyProfile,
      wakeTime: wakeTime
    };

    // Cache the result
    hostSettingsCache.set(cacheKey, settings);
    logger.debug(`Cached host settings for user ${userId}:`, {
      bufferBefore: settings.bufferBefore,
      bufferAfter: settings.bufferAfter,
      minNoticeTime: settings.minNoticeTime,
      timezone: settings.timezone,
      wakeTime: settings.wakeTime
    });
    
    return settings;

  } catch (err) {
    logger.error(`Exception fetching host settings: ${err}`);
    // Fallback to reasonable defaults
    const fallbackSettings: HostAvailabilitySettings = {
      bufferBefore: 15,
      bufferAfter: 15,
      minNoticeTime: 60,
      timezone: 'UTC',
      wakeTime: 7
    };
    hostSettingsCache.set(cacheKey, fallbackSettings, 60000); // Short cache for errors
    return fallbackSettings;
  }
}

/**
 * Clear settings cache for a specific user
 */
export function clearSettingsCache(userId: string): void {
  const cacheKey = `host_settings_${userId}`;
  hostSettingsCache.delete(cacheKey);
  logger.debug(`Cleared settings cache for user ${userId}`);
}

/**
 * Force clear settings cache with pattern matching
 */
export function forceClearSettingsCache(userId?: string): void {
  if (userId) {
    const pattern = `host_settings_${userId}`;
    hostSettingsCache.invalidatePattern(pattern);
    logger.debug(`Force cleared settings cache for user ${userId}`);
  } else {
    hostSettingsCache.forceClear();
    logger.debug('Force cleared all settings cache');
  }
}

/**
 * Get cache statistics
 */
export function getSettingsCacheStats() {
  return hostSettingsCache.getStats();
}

/**
 * Force refresh settings for a user (bypass cache)
 */
export async function refreshHostAvailabilitySettings(userId: string): Promise<HostAvailabilitySettings> {
  clearSettingsCache(userId);
  return fetchHostAvailabilitySettings(userId);
}
